import React from "react";
import { graphql, useFragment } from "react-relay";

import cx from "classnames";
import { noop } from "lodash";

import { ClippingControls, confirmDiscardClip } from "~/meetingRecorder/ClipTrimmer";
import { Transcript, type TranscriptRef } from "~/meetingRecorder/TranscriptPanel";
import TranscriptVideo from "~/meetingRecorder/TranscriptVideo";
import { useIsNarrowViewport } from "~/meetingRecorder/utils";
import { Modal } from "~/ui/legacyFellowIndex";
import type { ModalProps } from "~/ui/modals";

import type { ClipCutModal_meetingRecording$key } from "~/__generated__/ClipCutModal_meetingRecording.graphql";
import type { TranscriptPanel_note$key } from "~/__generated__/TranscriptPanel_note.graphql";
import type { TranscriptVideo_videoPlaybackSettings$key } from "~/__generated__/TranscriptVideo_videoPlaybackSettings.graphql";

import styles from "~/meetingRecorder/css/ClipModal.module.css";
import transcriptModalStyles from "~/meetingRecorder/css/TranscriptModal.module.css";

type ClipModalProps = ModalProps<unknown> & {
	meetingRecordingKey: ClipCutModal_meetingRecording$key;
	note: TranscriptPanel_note$key | null;
	videoPlaybackSettings: TranscriptVideo_videoPlaybackSettings$key;
	allowRecapDownload?: boolean;

	fromNote?: boolean;
	initialTimestamp?: number;
	initialEndTimestamp?: number;
};

const clipCutModalMeetingRecordingFragment = graphql`
	fragment ClipCutModal_meetingRecording on MeetingRecording {
		__typename
		id
		transcript
		meetingRecordedVideoDuration
		meetingRecordedVideoUrl
		subtitlesVttUrl
		thumbnailVttUrl
		ownerAccount {
			__typename
		}
		note {
			title
		}
		...TranscriptPanel_Transcript_meetingRecording @arguments(public: false)
		...TranscriptVideo_meetingRecording @arguments(public: false)
	}
`;

export function ClipCutModal({
	close,
	meetingRecordingKey,
	note,
	videoPlaybackSettings,
	allowRecapDownload,
	fromNote,
	initialTimestamp,
	initialEndTimestamp,
}: ClipModalProps) {
	const meetingRecording = useFragment(clipCutModalMeetingRecordingFragment, meetingRecordingKey);
	const videoRef = React.useRef<HTMLVideoElement>(null);
	const transcriptRef = React.useRef<TranscriptRef>(null);
	const narrowViewport = useIsNarrowViewport();

	const closeModalSaved = React.useCallback(() => {
		close(true);
	}, [close]);

	const closeModalDiscard = React.useCallback(async () => {
		const discardClip = await confirmDiscardClip();
		if (!discardClip) return;

		close(false);
	}, [close]);

	return (
		<Modal
			onClose={closeModalDiscard}
			width={narrowViewport ? "100%" : "67rem"}
			height={narrowViewport ? "100%" : "53rem"}
			className={styles.modal}
		>
			<Modal.Header noBottomBorder onClose={closeModalDiscard}>
				<Modal.Title className={styles.modalTitle} tabIndex={0}>
					Create a clip
				</Modal.Title>
			</Modal.Header>

			<Modal.Body noPadding className={transcriptModalStyles.recapModalBody}>
				<div key="preview" className={transcriptModalStyles.preview}>
					{!narrowViewport && (
						<div key="left-panel" className={cx(transcriptModalStyles.leftPanel, styles.leftPanel)}>
							<Transcript
								isClip
								ref={transcriptRef}
								note={note}
								viewer={null}
								meetingRecording={meetingRecording}
								transcriptDeletionReason={null}
								transcript={meetingRecording.transcript}
								onSegmentClick={noop}
								videoDuration={meetingRecording.meetingRecordedVideoDuration}
								closeModal={noop}
							/>
							<div className={transcriptModalStyles.dimmer} />
						</div>
					)}

					<div key="metadata" className={cx(styles.modalMetadata, transcriptModalStyles.metadata)}>
						<TranscriptVideo
							isClip
							inClippingMode
							ref={videoRef}
							meetingRecording={meetingRecording}
							videoPlaybackSettings={videoPlaybackSettings}
							thumbnailTrack={null}
							initialVideoTimestamp={initialTimestamp}
							isCollapsed={false}
							setIsCollapsed={noop}
							allowRecapDownload={allowRecapDownload}
						>
							<track label="Subtitles" kind="subtitles" src={meetingRecording.subtitlesVttUrl} />
							{meetingRecording.thumbnailVttUrl && (
								<track default label="thumbnails" kind="metadata" src={meetingRecording.thumbnailVttUrl} />
							)}
						</TranscriptVideo>

						{meetingRecording.meetingRecordedVideoDuration && (
							<ClippingControls
								videoRef={videoRef}
								recordingId={meetingRecording.id}
								exitClipMode={close}
								noteTitle={meetingRecording.note?.title || "Untitled Note"}
								onClipSaveChangeTab={closeModalSaved}
								videoDuration={meetingRecording.meetingRecordedVideoDuration}
								fromNote={fromNote}
								initialTimestamp={initialTimestamp}
								initialEndTimestamp={initialEndTimestamp}
							/>
						)}
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);
}
